import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { ClientReducerState } from 'client/reducers';
import { BBModel, StorySettingsType, StorySymbols } from 'types/story';
import { setStoryElements, setEditableState } from 'client/actions';
import { STORY_ELEMENTS_ID } from 'client/constants/common';
import { Context } from 'client/components/common/SelectionHint/SelectionHintContext';
import { TRAVERSE_TREE_TARGETS } from 'client/components/common/BuildingBlocks/utils/common';
import TreeRenderer from 'client/components/pages/Story/TreeRenderer';
import Styles from 'client/components/pages/Story/Styles';
import IframeMessageReceiver from '../IframeMessageReceiver';
import css from './StoryElements.scss';

const connector = connect(
	({ settings, card }: ClientReducerState) => ({
		cardHeight: settings.cardHeight,
		hasCard: card._id !== undefined,
	}),
	{ setStoryElements, setEditableState }
);

type State = {
	/*
	 Elements is rendered from state, not only from props, because of,
	 there is also an option to update elements data in admin from one of the CardEditor components
	 and show updates in preview. To provide updates only story elements and to avoid wasted rendering
	 a number of parents, all these updates pushed here to state. (@see updateStoryElements)
	 */
	elements?: BBModel[];
	symbols?: StorySymbols;
};

type OwnProps = {
	isEditor: boolean;
	elements: BBModel[];
	symbols?: StorySymbols;
};

type Props = ConnectedProps<typeof connector> & OwnProps;

class StoryElements extends Component<Props, State> {
	static contextType = Context;

	static getDerivedStateFromProps(props, state) {
		const isInitialRender = !state.elements && props.elements;

		// Update state from props only in a single case
		// 1. elements provided from props 1st time (initial render)
		if (isInitialRender) {
			return { elements: props.elements, symbols: props.symbols };
		}

		return null;
	}

	state: State = {};

	get treeOptions() {
		if (!this.props.isEditor) {
			return {
				target: TRAVERSE_TREE_TARGETS.DEFAULT,
				isCardTree: false,
			};
		}
		return {
			target: TRAVERSE_TREE_TARGETS.EDITOR,
			isCardTree: false,
			onMouseDown: this.context.treeOptions.onMouseDown,
			onFocus: this.context.treeOptions.onFocus,
			onMouseEnter: this.context.treeOptions.onMouseEnter,
			onMouseLeave: this.context.treeOptions.onMouseLeave,
			onMouseDownCapture: this.context.treeOptions.onMouseDownCapture,
			onDoubleClickCapture: this.context.treeOptions.onDoubleClickCapture,
		};
	}

	/**
	 * Update card from admin panel, by CardEditor components
	 * @param elements {Array<Object>}
	 * @param settings {Object} story settings
	 * @param symbols {Object} story symbols
	 * @param editableState {Object} editable element state model from admin
	 */
	updateStoryElements = ({
		elements,
		settings = {},
		symbols = {},
		editableState,
	}: {
		elements: BBModel[];
		settings: StorySettingsType;
		symbols: StorySymbols;
		editableState: any;
	}) => {
		this.setState({ elements, symbols });
		this.props.setEditableState(editableState);
		this.props.setStoryElements(elements);

		// Do i need store settings in redux? Probably yes to get access to symbols
		// this.props.setStorySettings(settings);
	};

	render() {
		const { elements, symbols } = this.state;

		if (!elements || !this.props.hasCard) {
			return null;
		}

		const { isEditor } = this.props;

		return (
			<div id={STORY_ELEMENTS_ID} className={css.storyElements} style={{ height: this.props.cardHeight }}>
				<TreeRenderer elements={elements} symbols={symbols} treeOptions={this.treeOptions} />
				<Styles elements={elements} symbols={symbols} isCardTree={false} isEditor={isEditor} />
				{isEditor && <IframeMessageReceiver isStory updateStoryElements={this.updateStoryElements} />}
			</div>
		);
	}
}

export default connector(StoryElements);
